import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, Grid, Paper, Box, List, ListItem, ListItemText, Button, Avatar, Card, CardContent, CardHeader, Chip, Stack, IconButton, Tooltip, CardActionArea, CardActions, ButtonBase, useMediaQuery } from '@mui/material';
// import * as faceapi from 'face-api.js';
import { alpha, useTheme } from '@mui/material/styles';
// Swiper for premium carousel
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Keyboard, EffectCoverflow } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
import { Link } from 'react-router-dom';
import { API_BASE } from '../config';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';

// team will be loaded from the backend API at runtime

const slides = [
  {
    image: 'https://images.unsplash.com/photo-1542744094-24638eff58bb?w=1600&q=80&auto=format&fit=crop',
    title: 'LLM Workshops & Training',
    bullets: [
      'Advanced prompt engineering techniques: zero-shot, few-shot, and chain-of-thought prompting strategies',
      'Fine-tuning pre-trained models using LoRA, QLoRA, and parameter-efficient methods for domain adaptation',
      'RAG (Retrieval-Augmented Generation) architecture design with vector databases and semantic search',
      'LLM safety, hallucination mitigation, content filtering, and responsible AI deployment practices',
      'Token optimization, context window management, and cost-effective inference strategies',
      'Integration with LangChain, LlamaIndex, and enterprise LLM orchestration frameworks'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1600&q=80&auto=format&fit=crop',
    title: 'Building RAG-powered Products',
    bullets: [
      'Document ingestion pipelines: chunking strategies, metadata extraction, and multi-format processing',
      'Vector database selection and optimization: Pinecone, Weaviate, Milvus, ChromaDB comparison',
      'Embedding models: OpenAI, Cohere, Sentence-BERT performance benchmarking and selection',
      'Hybrid search combining semantic similarity with keyword-based retrieval for improved accuracy',
      'Latency optimization techniques: caching, async processing, and streaming responses',
      'Production deployment: monitoring retrieval quality, A/B testing, and continuous improvement'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1600&q=80&auto=format&fit=crop',
    title: 'MLOps for LLMs',
    bullets: [
      'Model versioning and experiment tracking with MLflow, Weights & Biases, and DVC',
      'Automated evaluation frameworks: BLEU, ROUGE, perplexity, and custom domain-specific metrics',
      'CI/CD pipelines for LLM deployment: automated testing, validation, and rollback strategies',
      'Infrastructure as Code: Kubernetes orchestration, auto-scaling, and resource optimization',
      'Monitoring and observability: latency tracking, token usage, error rates, and user feedback loops',
      'Edge deployment optimization: model quantization, ONNX conversion, and TensorRT acceleration'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1600&q=80&auto=format&fit=crop',
    title: 'Spring Boot & Enterprise Java',
    bullets: [
      'Spring Boot 3.x features: GraalVM native image compilation, virtual threads (Project Loom), observability',
      'Jakarta EE 10 migration: namespace changes, Jakarta Persistence API 3.1, CDI 4.0 enhancements',
      'Reactive programming with Spring WebFlux, R2DBC for non-blocking database access',
      'Spring Security 6: OAuth 2.1, JWT authentication, method-level security, and CORS configuration',
      'Microservices patterns: circuit breakers with Resilience4j, distributed tracing with Micrometer',
      'Testing strategies: TestContainers, MockMvc, WebTestClient, and contract testing with Spring Cloud Contract'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&q=80&auto=format&fit=crop',
    title: 'Microservices Architecture',
    bullets: [
      'Service discovery and registration: Eureka, Consul, Kubernetes service mesh integration',
      'API Gateway patterns: Spring Cloud Gateway, rate limiting, authentication, and request routing',
      'Distributed tracing: Zipkin, Jaeger, OpenTelemetry implementation for end-to-end observability',
      'Event-driven architecture: Apache Kafka, RabbitMQ, and event sourcing patterns with Axon Framework',
      'Database per service pattern: managing distributed transactions with Saga pattern and eventual consistency',
      'Container orchestration: Docker containerization, Kubernetes deployment, Helm charts, and service mesh with Istio'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=1600&q=80&auto=format&fit=crop',
    title: 'Java Performance Optimization',
    bullets: [
      'JVM architecture deep dive: heap structure, stack memory, method area, and class loading mechanisms',
      'Garbage collection tuning: G1GC, ZGC, Shenandoah GC comparison, GC log analysis, and pause time optimization',
      'Memory leak detection and prevention: heap dumps, MAT (Memory Analyzer Tool), VisualVM profiling',
      'CPU profiling techniques: async-profiler, JFR (Java Flight Recorder), flame graphs for hotspot identification',
      'Concurrency optimization: thread pool tuning, lock contention analysis, and java.util.concurrent utilities',
      'JIT compiler optimization: tiered compilation, inlining, escape analysis, and code cache management'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1600&q=80&auto=format&fit=crop',
    title: 'Oracle PeopleSoft HCM',
    bullets: [
      'Core HR module configuration: organizational structure, position management, and employee self-service',
      'Talent acquisition lifecycle: requisition management, candidate tracking, interview scheduling, and offer letters',
      'Benefits administration: enrollment processing, life events, carrier interfaces, and compliance reporting',
      'Time and labor management: timekeeping, absence management, shift scheduling, and payroll integration',
      'Workforce analytics: custom reports with BI Publisher, dashboards, workforce planning, and predictive analytics',
      'PeopleSoft upgrade strategies: PeopleTools upgrades, selective adoption, testing frameworks, and migration best practices'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1600&q=80&auto=format&fit=crop',
    title: 'Oracle Retail Solutions',
    bullets: [
      'Xstore Point of Service: installation, configuration, transaction processing, and offline mode capabilities',
      'Xadmin administration: store hierarchy setup, item management, price changes, and promotion configuration',
      'Omnichannel integration: order capture, fulfillment options (BOPIS, ship-from-store), inventory visibility',
      'Payment gateway integration: EMV compliance, tokenization, gift cards, and loyalty program integration',
      'Mobile POS deployment: iPad/Android tablet configuration, line busting, endless aisle capabilities',
      'Reporting and analytics: sales analysis, inventory turnover, customer insights, and real-time dashboards'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1600&q=80&auto=format&fit=crop',
    title: 'Salesforce CRM Mastery',
    bullets: [
      'Sales Cloud implementation: opportunity management, forecasting, territory management, and CPQ configuration',
      'Service Cloud features: case management, omni-channel routing, knowledge base, and live agent chat',
      'Einstein AI capabilities: predictive lead scoring, opportunity insights, automated activity capture, and next-best-action',
      'Lightning Web Components (LWC): modern component development, JavaScript framework integration, testing',
      'Apex programming: triggers, batch processing, scheduled jobs, REST/SOAP API integration, and governor limits',
      'Integration patterns: MuleSoft Anypoint, Heroku Connect, platform events, and external object connectivity'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1600&q=80&auto=format&fit=crop',
    title: 'Pega CRM & BPM',
    bullets: [
      'Low-code application development: App Studio, case types, stages, processes, and UI design with Cosmos',
      'Decisioning and AI: adaptive models, predictive analytics, Next-Best-Action framework for real-time personalization',
      'Case management: parent-child cases, SLA management, correspondence, and approval workflows',
      'Robotic Process Automation (RPA): Pega Robotics integration, desktop automation, and intelligent automation',
      'Integration hub: REST/SOAP connectors, data transforms, queue processors, and event-driven architectures',
      'DevOps on Pega: branching strategies, continuous integration with Jenkins, deployment manager, and testing frameworks'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=1600&q=80&auto=format&fit=crop',
    title: 'Digital Marketing & SEO',
    bullets: [
      'AI-powered marketing automation: predictive analytics, customer segmentation, personalized content delivery',
      'Advanced SEO techniques: technical SEO audits, Core Web Vitals optimization, structured data markup (Schema.org)',
      'Content marketing strategy: keyword research with AI tools, content clustering, topic authority building',
      'Conversion rate optimization: A/B testing, heatmaps, user journey analysis, and multivariate testing',
      'Marketing analytics: Google Analytics 4, attribution modeling, customer lifetime value, ROI tracking',
      'Social media marketing: platform algorithms, paid advertising optimization, influencer collaboration, engagement metrics'
    ]
  },
  {
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1600&q=80&auto=format&fit=crop',
    title: 'Data Warehousing & Analytics',
    bullets: [
      'Modern data warehouse architecture: dimensional modeling (star/snowflake schemas), data vault methodology',
      'Cloud data platforms: Snowflake, BigQuery, Redshift comparison, auto-scaling, and cost optimization',
      'ETL/ELT pipelines: Apache Airflow orchestration, dbt transformations, data quality validation frameworks',
      'Real-time analytics: stream processing with Kafka, Flink, change data capture (CDC), and lambda architecture',
      'Business intelligence tools: Tableau, Power BI, Looker integration, semantic layers, and self-service analytics',
      'Data governance: metadata management, data lineage tracking, GDPR compliance, and data catalog implementation'
    ]
  }
];


// Placeholder for smart cropping using face-api.js (to be implemented)
function SmartAvatar({ src, alt, size }) {
  if (!src) {
    return <Avatar sx={{ width: size, height: size }}>{alt?.charAt(0) || '?'}</Avatar>;
  }
  // TODO: Integrate face-api.js for smart cropping
  return (
    <Box component="img" src={src} alt={alt} sx={{ width: size, height: size, borderRadius: 2, objectFit: 'cover', background: '#f5f7fa' }} />
  );
}

function TeamCarousel({ team, preset }) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // Enhanced preset values for modern UI
  const values = preset === 'A' ? {
    scale: 1.15,
    depth: 350,
    stretch: 100,
    modifier: 2.5,
    navSolid: true,
    navOffset: 20,
    transition: 400,
    imageSize: 200,
    spaceBetween: 70
  } : {
    scale: 1.08,
    depth: 250,
    stretch: 60,
    modifier: 1.5,
    navSolid: false,
    navOffset: 12,
    transition: 300,
    imageSize: 180,
    spaceBetween: 50
  };

  const { scale, depth, stretch, modifier, navSolid, navOffset, transition, imageSize, spaceBetween } = values;

  if (!team || team.length === 0) return null;

  return (
    <Box sx={(theme) => ({ 
      position: 'relative',
      py: 4,
      '& .swiper-button-prev, & .swiper-button-next': { 
        zIndex: 6, 
        width: 60, 
        height: 60, 
        borderRadius: '50%', 
        background: navSolid 
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          : 'rgba(255,255,255,0.9)',
        backdropFilter: 'blur(10px)',
        color: navSolid ? '#fff' : theme.palette.primary.main, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.18)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'scale(1.1)',
          boxShadow: '0 12px 40px rgba(102,126,234,0.3)'
        }
      }, 
      '& .swiper-button-prev': { left: navOffset }, 
      '& .swiper-button-next': { right: navOffset }, 
      '& .swiper-pagination-bullet': { 
        width: 12, 
        height: 12, 
        opacity: 0.6, 
        background: theme.palette.grey[400],
        transition: 'all 0.3s ease'
      }, 
      '& .swiper-pagination-bullet-active': { 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        opacity: 1,
        transform: 'scale(1.3)'
      }
    })}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay, Keyboard, EffectCoverflow]}
        effect="coverflow"
        centeredSlides={true}
        slidesPerView={1}
        spaceBetween={spaceBetween}
        loop={false}
        navigation
        pagination={{ clickable: true }}
        keyboard={{ enabled: true }}

        autoplay={{ delay: 4500, disableOnInteraction: true }}
        // dynamic 3D settings
        coverflowEffect={{ rotate: 0, stretch, depth, modifier, slideShadows: true }}
        aria-live="polite"
        style={{ paddingBottom: 24 }}
      >
        {team.map((member) => {
          const name = `${member.firstName || member.name || ''} ${member.lastName || ''}`.trim() || member.name || '';
          const role = member.designation || member.role || '';
          const avatarSrc = member.id ? `${API_BASE}/api/team/${member.id}/thumbnail` : null;
          const experience = member.experienceDetails || member.experience || '';
          const production = member.productionDetails || member.productDetails || '';
          const tagsSource = Array.isArray(member.skills) ? member.skills : (production || experience || member.bio || '');
          const tags = Array.isArray(tagsSource) ? tagsSource : (String(tagsSource).split(/[,;\n|]+/).map(s => s.trim()).filter(Boolean).slice(0,4));
          // Enhanced gradient backgrounds
          function getCardBg(role, skills) {
            if (role?.toLowerCase().includes('ai') || (skills && skills.join && skills.join(' ').toLowerCase().includes('ai'))) {
              return 'linear-gradient(135deg, rgba(102,126,234,0.95) 0%, rgba(124,77,255,0.95) 100%)';
            }
            if (role?.toLowerCase().includes('lead') || role?.toLowerCase().includes('cto')) {
              return 'linear-gradient(135deg, rgba(255,179,71,0.95) 0%, rgba(255,204,51,0.95) 100%)';
            }
            if (role?.toLowerCase().includes('engineer')) {
              return 'linear-gradient(135deg, rgba(67,206,162,0.95) 0%, rgba(24,90,157,0.95) 100%)';
            }
            if (role?.toLowerCase().includes('design')) {
              return 'linear-gradient(135deg, rgba(252,92,125,0.95) 0%, rgba(106,130,251,0.95) 100%)';
            }
            if (skills && skills.join && skills.join(' ').toLowerCase().includes('cloud')) {
              return 'linear-gradient(135deg, rgba(54,209,196,0.95) 0%, rgba(30,60,114,0.95) 100%)';
            }
            return 'linear-gradient(135deg, rgba(99,102,241,0.95) 0%, rgba(168,85,247,0.95) 100%)';
          }
          const cardBg = getCardBg(role, tags);
          return (
            <SwiperSlide key={member.id || name}>
              <Card
                className="team-card"
                sx={{
                  height: '100%',
                  minHeight: 420,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: `all ${transition}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                  background: cardBg,
                  backdropFilter: 'blur(20px) saturate(180%)',
                  boxShadow: '0 20px 60px 0 rgba(31,38,135,0.25)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  overflow: 'hidden',
                  position: 'relative',
                  borderRadius: 4,
                  '&:hover': {
                    transform: 'scale(1.05) translateY(-8px) rotateY(5deg)',
                    boxShadow: '0 30px 80px 0 rgba(31,38,135,0.35)',
                    border: '2px solid rgba(255,255,255,0.4)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                    zIndex: 0,
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    opacity: 0,
                    transition: 'opacity 0.5s ease',
                    zIndex: 1,
                  },
                  '&:hover::after': {
                    opacity: 1,
                  }
                }}
              >
                <CardActionArea component={Button} href={`/team/${member.slug}`} sx={{ flexGrow: 1, textAlign: 'left', alignItems: 'stretch', display: 'block', position: 'relative', zIndex: 2 }}>
                  {/* Name/Header with enhanced styling */}
                  <Box sx={{ px: 3, pt: 2, pb: 1, background: 'linear-gradient(to bottom, rgba(0,0,0,0.05), transparent)' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>{name}</Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600, mt: 0.5 }}>{role}</Typography>
                  </Box>

                  {/* Main content: enhanced layout */}
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'stretch', p: 2 }}>
                    <Box sx={{ width: { xs: '100%', sm: imageSize }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {member.id ? (
                        <SmartAvatar src={`${API_BASE}/api/team/${member.id}/photo`} alt={`Photo of ${name}`} size={imageSize} />
                      ) : (
                        <Avatar sx={{ width: imageSize, height: imageSize }}>{name.charAt(0)}</Avatar>
                      )}
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      {experience ? (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 1.5,
                            color: 'rgba(255,255,255,0.95)',
                            fontSize: '0.95rem',
                            fontWeight: 500
                          }}
                        >
                          {experience}
                        </Typography>
                      ) : null}
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1, gap: 1 }}>
                        {(tags || []).map((s) => (
                          <Chip 
                            key={s} 
                            label={s} 
                            size="small"
                            sx={{
                              background: 'rgba(255,255,255,0.25)',
                              backdropFilter: 'blur(10px)',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              border: '1px solid rgba(255,255,255,0.3)',
                              '&:hover': {
                                background: 'rgba(255,255,255,0.35)',
                                transform: 'translateY(-2px)',
                                transition: 'all 0.3s ease'
                              }
                            }}
                          />
                        ))}
                      </Stack>
                      {/* AI-generated highlight/fun fact placeholder */}
                      <Box 
                        sx={{ 
                          mt: 2, 
                          p: 1.5,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 100%)',
                          backdropFilter: 'blur(20px)',
                          borderRadius: 2,
                          border: '1px solid rgba(255,255,255,0.2)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'rgba(255,255,255,0.95)',
                            fontStyle: 'italic',
                            fontWeight: 500,
                            lineHeight: 1.6,
                            display: 'block'
                          }}
                        >
                          {/* TODO: Integrate OpenAI API for real highlight */}
                          {member.aiHighlight || '“Fun fact: This team member is passionate about innovation and teamwork!”'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardActionArea>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  {member.social?.linkedin && (
                    <Tooltip title="LinkedIn">
                      <IconButton 
                        component="a" 
                        href={member.social.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                          color: 'white',
                          '&:hover': {
                            background: 'rgba(255,255,255,0.3)',
                            transform: 'scale(1.1) translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <LinkedInIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {member.social?.twitter && (
                    <Tooltip title="Twitter">
                      <IconButton 
                        component="a" 
                        href={member.social.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        sx={{
                          background: 'rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)',
                          color: 'white',
                          '&:hover': {
                            background: 'rgba(255,255,255,0.3)',
                            transform: 'scale(1.1) translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <TwitterIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {/* Enhanced active slide scaling with 3D effects */}
      <Box component="style">
        {` 
          .swiper-container .swiper-slide-active .team-card { 
            transform: scale(${scale}) translateZ(50px); 
            box-shadow: 0 30px 80px rgba(0,0,0,0.25);
            filter: brightness(1.05);
          }
          .swiper-container .swiper-slide-prev .team-card,
          .swiper-container .swiper-slide-next .team-card {
            filter: brightness(0.8) blur(1px);
            opacity: 0.7;
          }
        `}
      </Box>
    </Box>
  );
}

function SlideShow({ slides }) {
  if (!slides || slides.length === 0) return null;

  const navOffset = 20;
  const navSolid = true;
  const activeScale = 1.03;

  return (
    <Box sx={(theme) => ({ borderRadius: 2, overflow: 'hidden', position: 'relative', '& .swiper-button-prev, & .swiper-button-next': { zIndex: 6, width: 56, height: 56, borderRadius: '50%', backgroundColor: navSolid ? theme.palette.primary.main : 'rgba(2,136,209,0.08)', color: navSolid ? '#fff' : theme.palette.primary.main, boxShadow: theme.shadows[3], display: 'flex', alignItems: 'center', justifyContent: 'center' }, '& .swiper-button-prev': { left: navOffset }, '& .swiper-button-next': { right: navOffset }, '& .swiper-pagination-bullet': { width: 10, height: 10, opacity: 0.8, background: theme.palette.grey[300] }, '& .swiper-pagination-bullet-active': { background: theme.palette.primary.main } })}>
      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 1, md: 0 } }}>
        <Swiper
          modules={[Navigation, Pagination, Autoplay, Keyboard]}
          slidesPerView={1}
          spaceBetween={24}
          navigation
          pagination={{ clickable: true }}
          keyboard={{ enabled: true }}

          autoplay={{ delay: 4800, disableOnInteraction: true }}
          style={{ paddingBottom: 24 }}
        >
          {slides.map((s, idx) => (
            <SwiperSlide key={idx}>
              <Card className="slide-card" sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: 2, p: { xs: 2, md: 3 }, boxShadow: 0, borderRadius: 2 }}>
                <Box sx={{ flex: 1, minHeight: 220, borderRadius: 2, overflow: 'hidden', backgroundColor: (t) => t.palette.grey[100] }}>
                  <Box component="img" src={s.image} alt={s.title} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </Box>

                <Box sx={{ width: { xs: '100%', md: 440 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{s.title}</Typography>
                  <List>
                    {s.bullets.map((b) => (
                      <ListItem key={b} disableGutters sx={{ py: 0.5 }}>
                        <ListItemText primary={b} primaryTypographyProps={{ variant: 'body2' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>

      <Box component="style">
        {` .swiper-container .swiper-slide-active .slide-card { transform: scale(${activeScale}); box-shadow: 0px 18px 42px rgba(0,0,0,0.08); }`}
      </Box>
    </Box>
  );
}

export default function About() {
  const [team, setTeam] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [preset, setPreset] = useState('A'); // 'A' = Bold 3D, 'B' = Subtle

  useEffect(() => {
    let mounted = true;
    setLoadingTeam(true);
    fetch(`${API_BASE}/api/team`)
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(list => { if (mounted && Array.isArray(list)) setTeam(list); })
      .catch(err => { console.error('Failed to load team list', err); if (mounted) setTeam([]); })
      .finally(() => { if (mounted) setLoadingTeam(false); });
    return () => { mounted = false; };
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ 
        textAlign: 'center', 
        mb: 8,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -40,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 80,
          height: 4,
          background: 'linear-gradient(90deg, #7C4DFF 0%, #448AFF 100%)',
          borderRadius: 2
        }
      }}>
        <Typography 
          variant="h2" 
          sx={{ 
            fontWeight: 800, 
            mb: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}
        >
          About Neha Information Technology
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'text.secondary', 
            maxWidth: 800, 
            mx: 'auto',
            lineHeight: 1.8,
            fontWeight: 400
          }}
        >
          We partner with ambitious organisations to design, build and operate modern software that drives measurable business value.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper 
            sx={{ 
              p: 4, 
              height: '100%',
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
              }
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>👥</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Who we are</Typography>
              </Box>
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                NehaInfoTech is a pragmatic software engineering firm founded to help businesses adopt modern software practices and AI-driven products. We combine product thinking, experienced engineering teams and a data-first approach to deliver solutions that scale.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>🎯</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Our focus</Typography>
              </Box>
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                We specialise in end-to-end software delivery, cloud-native architectures, data engineering and operationalizing Generative AI and large language models (LLMs). Our services include development, managed support, data analytics, and training programs to upskill teams for the AI era.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>🤖</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Why GenAI & LLMs</Typography>
              </Box>
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                Generative AI and LLMs are transforming how products interact with users and automate cognitive tasks. We help companies apply LLMs responsibly — from prototyping use-cases to production-grade deployments with observability, safety controls and cost optimisation.
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>⚡</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>What we do</Typography>
              </Box>
              <Stack spacing={2}>
                {[
                  { icon: '💻', text: 'Custom Software Development: Web, mobile and cloud-native systems' },
                  { icon: '🛡️', text: 'Managed Support & SRE: 24x7 support, incident response and reliability engineering' },
                  { icon: '📊', text: 'Data & Analytics: pipelines, model ops, and production ML systems' },
                  { icon: '🧠', text: 'Generative AI & LLM Integration: prototypes, agents, retrieval-augmented generation (RAG) and governance' },
                  { icon: '🎓', text: 'Training & Workshops: hands-on LLM workshops, MLOps and software engineering best practices' }
                ].map((item, idx) => (
                  <Box 
                    key={idx}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: alpha('#667eea', 0.04),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#667eea', 0.08),
                        transform: 'translateX(8px)'
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: '1.5rem', mr: 2, minWidth: 32 }}>{item.icon}</Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>{item.text}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Paper 
              sx={{ 
                p: 3,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>🚀</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Our approach</Typography>
              </Box>
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                We prefer short discovery sprints, measurable milestones and iterative delivery. We emphasise test automation, observability and secure defaults so your product improves safely in production.
              </Typography>
            </Paper>

            <Paper 
              sx={{ 
                p: 3,
                boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 2
                }}>
                  <Typography sx={{ color: 'white', fontSize: '1.5rem' }}>💎</Typography>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Core values</Typography>
              </Box>
              <Stack spacing={2}>
                {[
                  { icon: '🎯', text: 'Customer-first: outcomes over features' },
                  { icon: '⚙️', text: 'Engineering discipline: reliable, maintainable systems' },
                  { icon: '🔒', text: 'Responsible AI: privacy, fairness and safety' },
                  { icon: '📚', text: 'Continuous learning: we share knowledge and train teams' }
                ].map((item, idx) => (
                  <Box 
                    key={idx}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: 'background.paper',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha('#667eea', 0.08),
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    <Typography sx={{ fontSize: '1.2rem', mr: 1.5 }}>{item.icon}</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{item.text}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>

            <Box sx={{ textAlign: 'center' }}>
              <Button 
                variant="contained" 
                component="a" 
                href="/contact"
                size="large"
                sx={{ 
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Talk to an expert
              </Button>
            </Box>
          </Stack>
        </Grid>
      </Grid>

        {/* Slides / Training embed (local slideshow) */}
        <Box sx={{ mt: 6 }}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Training & Workshop Slides
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
            Explore our comprehensive training programs covering AI/ML, Enterprise Java, Oracle solutions, CRM platforms, Digital Marketing, and more. 
            Our hands-on workshops equip your team with cutting-edge skills and practical knowledge.
          </Typography>
          <SlideShow slides={slides} />
        </Box>

        {/* Client logos / trust signals */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1" gutterBottom>Trusted by</Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=200&q=60&auto=format&fit=crop" alt="client" style={{ height: 48, opacity: 0.9 }} />
            <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=200&q=60&auto=format&fit=crop" alt="client" style={{ height: 48, opacity: 0.9 }} />
            <img src="https://images.unsplash.com/photo-1492724441997-5dc865305da7?w=200&q=60&auto=format&fit=crop" alt="client" style={{ height: 48, opacity: 0.9 }} />
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" href="/training-deck.pdf" target="_blank">Download Training Deck (PDF)</Button>
          </Box>
        </Box>

        {/* Our Values Section */}
        <Box sx={{ mt: 8, mb: 6 }}>
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={5}>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 2, fontWeight: 600, mb: 1, display: 'block' }}>
                OUR VALUES
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.3 }}>
                We Build. We Innovate.<br />We Care. We Listen.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.9, fontSize: '1.1rem' }}>
                There are few streams of technology that evolve at a pace as rapid as digital does. It is easy here to get bogged down with the daily task sheets, tech updates, deliveries and business acquisitions. But it is important to try not to miss the forest for the trees--to not to miss the big picture of why we are here. Our core values and principles continue to align us to the track of excellence we wanted to pursue when we started out, while also enabling us to rapidly serve the needs of diverse industries and requirements.
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={4}>
            {/* Adaptability */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                p: 4, 
                height: '100%', 
                boxShadow: 2,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}>
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: alpha('#7C4DFF', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Box sx={{ 
                    fontSize: 32,
                    color: '#7C4DFF'
                  }}>⚙️</Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Adaptability
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  A business is only as young as it is flexible. Be willing to experiment, try new permutations of skills or solutions and take the road less travelled to pursue excellence.
                </Typography>
              </Card>
            </Grid>

            {/* A Sense of Gratitude */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                p: 4, 
                height: '100%', 
                boxShadow: 2,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}>
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: alpha('#7C4DFF', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Box sx={{ 
                    fontSize: 32,
                    color: '#7C4DFF'
                  }}>👥</Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  A Sense of Gratitude
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  A lot of factors have gotten us to where we are today--our employee's efforts, our client's trust, and the open-source contributions of several bright minds across the world. We owe them all.
                </Typography>
              </Card>
            </Grid>

            {/* Ownership */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                p: 4, 
                height: '100%', 
                boxShadow: 2,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}>
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: alpha('#7C4DFF', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Box sx={{ 
                    fontSize: 32,
                    color: '#7C4DFF'
                  }}>🤝</Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Ownership
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Every person working on a project should have the freedom to take ownership and find how they can best contribute, without getting bogged down with red tape or a narrow vision.
                </Typography>
              </Card>
            </Grid>

            {/* Smart Work */}
            <Grid item xs={12} sm={6}>
              <Card sx={{ 
                p: 4, 
                height: '100%', 
                boxShadow: 2,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 6
                }
              }}>
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: alpha('#7C4DFF', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3
                }}>
                  <Box sx={{ 
                    fontSize: 32,
                    color: '#7C4DFF'
                  }}>💡</Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  Smart Work
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                  Hard work and smart work are not mutually exclusive. Make the latter guide the former. Let efforts get focused in the right direction.
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ 
          mt: 8, 
          position: 'relative', 
          px: { xs: 1, md: 0 },
          py: 6,
          borderRadius: 4,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)'
        }}>
          <Box sx={{ position: { xs: 'static', md: 'absolute' }, right: { md: 24 }, top: { md: 24 }, display: 'flex', gap: 1, alignItems: 'center', mb: { xs: 2, md: 0 }, zIndex: 1 }}>
            <Typography variant="caption" sx={{ mr: 1, color: 'text.secondary', fontWeight: 600 }}>View Style:</Typography>
            <Button 
              variant={preset === 'A' ? 'contained' : 'outlined'} 
              size="small" 
              onClick={() => setPreset('A')}
              sx={{
                background: preset === 'A' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                '&:hover': {
                  background: preset === 'A' ? 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' : 'rgba(102, 126, 234, 0.08)'
                }
              }}
            >
              Bold (A)
            </Button>
            <Button 
              variant={preset === 'B' ? 'contained' : 'outlined'} 
              size="small" 
              onClick={() => setPreset('B')}
              sx={{
                background: preset === 'B' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                '&:hover': {
                  background: preset === 'B' ? 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' : 'rgba(102, 126, 234, 0.08)'
                }
              }}
            >
              Subtle (B)
            </Button>
          </Box>

          <Box sx={{ 
            textAlign: 'center', 
            maxWidth: 920, 
            mx: 'auto',
            mb: 5
          }}>
            <Box sx={{ 
              display: 'inline-block',
              mb: 2
            }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
              }}>
                <Typography sx={{ fontSize: '2rem' }}>👥</Typography>
              </Box>
            </Box>
            
            <Typography 
              variant="h3" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Leadership & Team
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ 
                mb: 2,
                lineHeight: 1.6,
                fontWeight: 400
              }}
            >
              Experienced leaders in product, engineering, data science and GenAI
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.8
              }}
            >
              Our diverse team brings decades of combined expertise from leading technology companies, 
              driving innovation and delivering exceptional results for our clients.
            </Typography>
          </Box>

          {/* Team carousel (primary view) */}
          <Box sx={{ 
            mt: 4,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -32,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 100,
              height: 4,
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2
            }
          }}>
            <TeamCarousel team={team} preset={preset} />
          </Box>
        </Box>
    </Container>
  );
}
